import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, Plus, Trash2, Calendar, DollarSign, 
  Building2, Cloud, Phone, Loader2, Database,
  History, FileText, Upload, X, ExternalLink, Eye, Download,
  Search, Layers, Filter, Tags, RefreshCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default Service Types (fallback)
const DEFAULT_SERVICE_TYPES = [
  { id: 'google_workspace', name: 'Google Workspace', icon: 'Cloud', color: '#4285F4' },
  { id: 'emergent', name: 'Emergent', icon: 'Building2', color: '#8B5CF6' },
  { id: 'twilio', name: 'Twilio', icon: 'Phone', color: '#F22F46' },
  { id: 'mongodb_atlas', name: 'MongoDB Atlas', icon: 'Database', color: '#00ED64' },
  { id: 'rocket_ai', name: 'Rocket AI', icon: 'Building2', color: '#FF6B35' },
  { id: 'godaddy', name: 'GoDaddy', icon: 'Cloud', color: '#1BDBDB' }
];

// Icon mapping
const ICON_MAP = {
  'Cloud': Cloud,
  'Building2': Building2,
  'Phone': Phone,
  'Database': Database
};

// Currency Options for Payment
const CURRENCY_OPTIONS = [
  { code: 'AED', name: 'درهم إماراتي', symbol: 'د.إ' },
  { code: 'USD', name: 'دولار أمريكي', symbol: '$' },
  { code: 'EUR', name: 'يورو', symbol: '€' },
  { code: 'GBP', name: 'جنيه إسترليني', symbol: '£' },
  { code: 'JPY', name: 'ين ياباني', symbol: '¥' },
  { code: 'SAR', name: 'ريال سعودي', symbol: 'ر.س' },
  { code: 'KWD', name: 'دينار كويتي', symbol: 'د.ك' },
  { code: 'QAR', name: 'ريال قطري', symbol: 'ر.ق' },
  { code: 'BHD', name: 'دينار بحريني', symbol: 'د.ب' },
  { code: 'OMR', name: 'ريال عماني', symbol: 'ر.ع' }
];

export default function ServicePayments() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  // iter-237.6 — mode split
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get('mode') || '').toLowerCase();
  const isEntryMode = mode === 'entry';
  const isRecordsMode = mode === 'records';
  const parentPath = isEntryMode
    ? '/finance/asb606/payments/entry'
    : isRecordsMode
      ? '/finance/asb606/payments/records'
      : '/finance/asb606/payments';
  const parentLabelAr = isEntryMode
    ? 'الإدراج المالي للشركة'
    : isRecordsMode
      ? 'السجل والمتابعة'
      : 'المدفوعات';
  const parentLabelEn = isEntryMode
    ? 'Financial Entry'
    : isRecordsMode
      ? 'Records & Tracking'
      : 'Payments';

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState([]);
  
  // View invoice details modal
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Exchange rates from Admin Panel (NEW - list based)
  const [ratesList, setRatesList] = useState([]);
  
  // Payment Services (الجهات/المنصات) - Dynamic from API
  const [paymentServices, setPaymentServices] = useState([]);

  // iter-218o (Feb 2026) — Entity types + filter state.
  const [entityTypes, setEntityTypes] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    entity_id: '',
    entity_type_id: '',
    date_from: '',
    date_to: '',
  });

  // New payment form (kept for legacy save; the actual UI now lives on the
  // full-page /finance/asb606/payments/company/add screen).
  const [newPayment, setNewPayment] = useState({
    service_type: '',
    amount: '',
    reason_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    invoice_file_url: '',
    notes: ''
  });

  // iter-217b — CRITICAL FIX: `getAuthHeaders()` returns a NEW object each call.
  // Previously `headers` was recreated on every render → every useCallback below
  // received a "new" dep → every useEffect re-fired → infinite render/fetch loop
  // (the exact "flicker + never-ending loader" the admin reported on this page).
  // Solution: build a stable header getter that only depends on `getAuthHeaders`
  // (which is stable inside AuthContext) via useCallback.
  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/payment-services`, { headers: authHeaders() });
      setPaymentServices(res.data.data || []);
    } catch (err) {
      console.log('Error fetching services:', err);
      // Use defaults as fallback
      setPaymentServices(DEFAULT_SERVICE_TYPES);
    }
  }, [authHeaders]);

  // iter-218o — Entity types dropdown (shared with the entity-types admin page).
  const fetchEntityTypes = useCallback(async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/dropdowns/entity-types`,
        { headers: authHeaders(), params: { active_only: true } },
      );
      setEntityTypes(r.data.data || []);
    } catch { /* ignore — filter just hides if empty */ }
  }, [authHeaders]);

  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/service-rates`, { headers: authHeaders() });
      setRatesList(res.data.data || []);
    } catch (err) {
      console.log('Error fetching rates:', err);
    }
  }, [authHeaders]);

  const fetchReasons = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/payment-reasons`, { headers: authHeaders() });
      setReasons(res.data.data || []);
    } catch (err) {
      console.log('Error fetching reasons:', err);
    }
  }, [authHeaders]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/finance/service-payments`, { headers: authHeaders() });
      setPayments(res.data.data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPayments();
    fetchRates();
    fetchReasons();
    fetchServices();
    fetchEntityTypes();
  }, [fetchPayments, fetchRates, fetchReasons, fetchServices, fetchEntityTypes]);

  const handleAddPayment = async () => {
    // iter-217c — Deprecated: the modal-based flow was replaced by the full page
    // /finance/asb606/payments/company/add. This shell is kept only to avoid
    // breaking any lingering imports.
    toast.info(language === 'ar' ? 'تم نقل الإضافة إلى صفحة مستقلة' : 'Add flow moved to a dedicated page');
  };

  // Get rate for selected service and currency (legacy rates list fallback)
  const getRate = (serviceId, currency) => {
    if (currency === 'AED') return 1; // No conversion needed
    const rate = ratesList.find(r => r.service_id === serviceId && r.currency === currency);
    return rate ? rate.rate_to_aed : null;
  };

  const deletePayment = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/finance/service-payments/${id}`, { headers: authHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      fetchPayments();
    } catch (err) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const getServiceInfo = (serviceType) => {
    const service = paymentServices.find(s => s.id === serviceType);
    if (service) {
      const IconComponent = ICON_MAP[service.icon] || Building2;
      return {
        id: service.id,
        nameAr: service.name_ar || service.name,
        nameEn: service.name_en || service.name,
        icon: IconComponent,
        color: service.color,
        bgColor: `${service.color}20`
      };
    }
    // Fallback to defaults
    const defaultService = DEFAULT_SERVICE_TYPES.find(s => s.id === serviceType);
    if (defaultService) {
      const IconComponent = ICON_MAP[defaultService.icon] || Building2;
      return {
        id: defaultService.id,
        nameAr: defaultService.name,
        nameEn: defaultService.name,
        icon: IconComponent,
        color: defaultService.color,
        bgColor: `${defaultService.color}20`
      };
    }
    return {
      id: serviceType,
      nameAr: serviceType,
      nameEn: serviceType,
      icon: Building2,
      color: '#808080',
      bgColor: '#80808020'
    };
  };

  const getCurrencyInfo = (code) => {
    return CURRENCY_OPTIONS.find(c => c.code === code) || { code, name: code, symbol: code };
  };

  // View invoice details
  const handleViewInvoice = (payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  // Download invoice file
  const handleDownloadFile = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'invoice';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(language === 'ar' ? 'جاري التحميل...' : 'Downloading...');
    } catch (err) {
      // If direct download fails, open in new tab
      window.open(fileUrl, '_blank');
    }
  };

  // iter-218o — Filtered payments + derived totals (react to filter changes).
  const filteredPayments = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return payments.filter((p) => {
      if (filters.entity_id && p.service_type !== filters.entity_id) return false;
      if (filters.entity_type_id) {
        const ent = paymentServices.find(s => s.id === p.service_type);
        if ((ent?.entity_type_id || '') !== filters.entity_type_id) return false;
      }
      if (filters.date_from && (p.payment_date || '') < filters.date_from) return false;
      if (filters.date_to   && (p.payment_date || '') > filters.date_to)   return false;
      if (q) {
        const hay = [
          p.invoice_number, p.description, p.reason_name_ar, p.reason_name_en,
          p.notes, p.service_type,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [payments, paymentServices, filters]);

  const totalAED = filteredPayments.reduce((sum, p) => sum + (p.amount_aed || 0), 0);
  const filtersActive = !!(filters.search || filters.entity_id || filters.entity_type_id || filters.date_from || filters.date_to);
  const resetFilters = () => setFilters({ search: '', entity_id: '', entity_type_id: '', date_from: '', date_to: '' });

  // Handle back
  const handleBack = () => {
    // Check current path to determine back navigation
    const currentPath = window.location.pathname;
    if (currentPath.includes('/cloudgm/customers')) {
      navigate('/dashboard/finance/cloudgm/customers');
    } else if (currentPath.includes('/cloudgm/merchants')) {
      navigate('/dashboard/finance/cloudgm/merchants');
    } else {
      // iter-237.6 — Route back to the correct parent (Entry / Records / Payments hub).
      navigate(parentPath);
    }
  };

  // Get breadcrumb based on current path (DashboardLayout format)
  const getBreadcrumb = () => {
    const currentPath = window.location.pathname;

    if (currentPath.includes('/cloudgm/customers')) {
      return [
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة العملاء (المالية)', labelEn: 'Customer Platform (Finance)', path: '/dashboard/finance/cloudgm/customers' },
        { labelAr: 'المدفوعات', labelEn: 'Payments' },
      ];
    }
    if (currentPath.includes('/cloudgm/merchants')) {
      return [
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)', labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'المدفوعات', labelEn: 'Payments' },
      ];
    }

    return [
      { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
      { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
      { labelAr: parentLabelAr, labelEn: parentLabelEn, path: parentPath },
      { labelAr: 'مدفوعات الشركة', labelEn: 'Company Payments' },
    ];
  };

  // Selected entity — currency & exchange rate come from القوائم المنسدلة
  const selectedEntity = paymentServices.find(s => s.id === newPayment.service_type) || null;
  const entityCurrency = selectedEntity?.currency && selectedEntity.currency !== 'AED' ? selectedEntity.currency : 'AED';
  const entityRate = entityCurrency === 'AED'
    ? 1
    : (selectedEntity?.exchange_rate || getRate(newPayment.service_type, entityCurrency));

  return (
    <DashboardLayout
      title="مدفوعات الشركة"
      titleEn="Company Payments"
      subtitle="مدفوعات الخدمات والاشتراكات (Google Workspace، Emergent، Twilio، MongoDB...)"
      subtitleEn="Services & subscriptions (Google Workspace, Emergent, Twilio, MongoDB...)"
      screenCode="ASB-FIN-PAY-CO"
      backPath={parentPath}
      onBack={handleBack}
      breadcrumb={getBreadcrumb()}
    >
      {/* Main Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header tiles — entry mode: Add Invoice + Bulk Add only (total lives in the records page);
              records mode: total only; legacy hub (no mode): all three. */}
          <div className={`grid grid-cols-1 gap-4 items-stretch ${isEntryMode ? 'md:grid-cols-2' : 'md:grid-cols-3'}`} data-testid="company-payments-header-tiles">
            {/* Add Invoice — iter-217c opens the dedicated add page. */}
            {!isRecordsMode && (
            <button
              onClick={() => navigate(`/finance/asb606/payments/company/add${isEntryMode ? '?mode=entry' : ''}`)}
              className="group rounded-2xl border border-blue-500/40 bg-gradient-to-r from-blue-600/30 to-blue-500/10 p-5 h-24 flex items-center justify-between text-start hover:from-blue-600/45 hover:to-blue-500/20 transition-all"
              data-testid="btn-add-payment"
            >
              <div>
                <p className="text-blue-200 text-sm mb-1">
                  {language === 'ar' ? 'إضافة فاتورة' : 'Add Invoice'}
                </p>
                <p className="text-white text-lg font-bold">
                  {language === 'ar' ? 'فاتورة جديدة' : 'New invoice'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                <Plus className="h-6 w-6 text-blue-200" />
              </div>
            </button>
            )}

            {/* Bulk Add — iter-218o opens the new bulk page. */}
            {!isRecordsMode && (
            <button
              onClick={() => navigate(`/finance/asb606/payments/company/bulk-add${isEntryMode ? '?mode=entry' : ''}`)}
              className="group rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-600/30 to-emerald-500/10 p-5 h-24 flex items-center justify-between text-start hover:from-emerald-600/45 hover:to-emerald-500/20 transition-all"
              data-testid="btn-bulk-add-payment"
            >
              <div>
                <p className="text-emerald-200 text-sm mb-1">
                  {language === 'ar' ? 'إضافة دفعات' : 'Bulk add'}
                </p>
                <p className="text-white text-lg font-bold">
                  {language === 'ar' ? 'حتى 50 إيصالاً' : 'Up to 50 receipts'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                <Layers className="h-6 w-6 text-emerald-200" />
              </div>
            </button>
            )}

            {/* Total Card — hidden in entry-only mode (it belongs to the records page). */}
            {!isEntryMode && (
            <div className="rounded-2xl border border-[#c9a227]/30 bg-gradient-to-r from-[#c9a227]/20 to-[#d4af37]/10 p-5 h-24 flex items-center justify-between" data-testid="total-payments-card">
              <div>
                <p className="text-[#c9a227] text-sm mb-1">
                  {language === 'ar' ? 'إجمالي المدفوعات بالدرهم' : 'Total Payments in AED'}
                </p>
                <p className="text-white text-lg font-bold">
                  {totalAED.toLocaleString('ar-AE', { minimumFractionDigits: 2 })} <span className="text-sm text-[#c9a227]">د.إ</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#c9a227]/20 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-[#c9a227]" />
              </div>
            </div>
            )}
          </div>

          {/* iter-218o — Filters row (iter-218p: rounded-full pill inputs). */}
          {/* iter-237.6 — Hide filters + list in entry-only mode. */}
          {!isEntryMode && (
          <div className="mt-4 rounded-2xl bg-white/[0.02] border border-white/10 p-4" data-testid="payments-filters">
            <div className="flex items-center gap-2 mb-3 text-white/60 text-xs">
              <Filter className="w-4 h-4" />
              <span className="font-medium">{language === 'ar' ? 'الفلاتر' : 'Filters'}</span>
              {filtersActive && (
                <button onClick={resetFilters}
                        className="ms-auto inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 text-[11px]"
                        data-testid="payments-filters-reset">
                  <RefreshCcw className="w-3 h-3" />
                  {language === 'ar' ? 'إعادة تعيين' : 'Reset'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {/* Search */}
              <div className="relative md:col-span-2">
                <Search className="absolute inset-y-0 start-4 my-auto w-4 h-4 text-white/40 pointer-events-none" />
                <input
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder={language === 'ar' ? 'بحث بالرقم / السبب / الملاحظات…' : 'Search by number / reason / notes…'}
                  className="w-full bg-[#050505] border border-white/10 rounded-full h-10 ps-10 pe-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                  data-testid="payments-filter-search"
                />
              </div>
              {/* Entity filter */}
              <select
                value={filters.entity_id}
                onChange={(e) => setFilters(f => ({ ...f, entity_id: e.target.value }))}
                className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                data-testid="payments-filter-entity"
              >
                <option value="">{language === 'ar' ? 'كل الجهات' : 'All entities'}</option>
                {paymentServices.map(s => (
                  <option key={s.id} value={s.id}>
                    {language === 'ar' ? (s.name_ar || s.name) : (s.name_en || s.name)}
                  </option>
                ))}
              </select>
              {/* Entity type filter */}
              <select
                value={filters.entity_type_id}
                onChange={(e) => setFilters(f => ({ ...f, entity_type_id: e.target.value }))}
                className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                data-testid="payments-filter-entity-type"
              >
                <option value="">{language === 'ar' ? 'كل أنواع الجهات' : 'All entity types'}</option>
                {entityTypes.map(et => (
                  <option key={et.id} value={et.id}>
                    {language === 'ar' ? (et.name_ar || et.name_en) : (et.name_en || et.name_ar)}
                  </option>
                ))}
              </select>
              {/* Date from */}
              <input type="date" value={filters.date_from}
                     onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40"
                     data-testid="payments-filter-date-from" dir="ltr" />
              {/* Date to */}
              <input type="date" value={filters.date_to}
                     onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                     className="bg-[#050505] border border-white/10 rounded-full h-10 px-4 text-sm text-white focus:outline-none focus:border-[#c9a227]/40 md:col-start-5"
                     data-testid="payments-filter-date-to" dir="ltr" />
            </div>
            <div className="mt-2 text-[11px] text-white/50">
              {language === 'ar'
                ? `النتائج: ${filteredPayments.length} من ${payments.length} فاتورة${filtersActive ? ' — الإجمالي بعد الفلترة أدناه' : ''}`
                : `Showing ${filteredPayments.length} of ${payments.length} invoices${filtersActive ? ' — total below reflects the filter' : ''}`}
            </div>
          </div>
          )}

          {/* Payments List — hidden in entry-only mode (iter-237.6) */}
          {!isEntryMode && (
          <div className="mt-6 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-blue-500" />
              {language === 'ar' ? 'سجل الفواتير' : 'Invoice History'}
              <span className="text-sm text-[#808080] font-normal mr-2">({filteredPayments.length}{filtersActive ? ` / ${payments.length}` : ''})</span>
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-12 bg-[#0a0a0a] rounded-xl border border-white/5">
                <CreditCard className="h-12 w-12 mx-auto mb-3 text-blue-500/30" />
                <p className="text-[#808080]">
                  {filtersActive
                    ? (language === 'ar' ? 'لا نتائج تطابق الفلاتر الحالية' : 'No results match the current filters')
                    : (language === 'ar' ? 'لا توجد فواتير' : 'No invoices yet')}
                </p>
                <p className="text-sm text-[#606060] mt-1">
                  {filtersActive
                    ? (language === 'ar' ? 'جرّب إعادة تعيين الفلاتر' : 'Try resetting the filters')
                    : (language === 'ar' ? 'اضغط على "إضافة فاتورة" لبدء التسجيل' : 'Click "Add Invoice" to start recording')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPayments.map((payment, index) => {
                  const service = getServiceInfo(payment.service_type);
                  const currency = getCurrencyInfo(payment.currency);
                  const Icon = service.icon;
                  
                  return (
                    <motion.div
                      key={payment.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        {/* Service Icon */}
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: service.bgColor }}
                        >
                          <Icon className="h-6 w-6" style={{ color: service.color }} />
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-white font-medium">
                              {language === 'ar' ? service.nameAr : service.nameEn}
                            </span>
                            {/* iter-218o — Entity type pill (from the shared dropdown) */}
                            {(() => {
                              const ent = paymentServices.find(s => s.id === payment.service_type);
                              const etAr = ent?.entity_type_ar;
                              const etEn = ent?.entity_type_en;
                              if (!etAr && !etEn) return null;
                              return (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-200">
                                  <Tags className="w-2.5 h-2.5" />
                                  {language === 'ar' ? (etAr || etEn) : (etEn || etAr)}
                                </span>
                              );
                            })()}
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: service.bgColor, color: service.color }}>
                              {language === 'ar' ? (payment.reason_name_ar || payment.description) : (payment.reason_name_en || payment.description)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-[#606060]">
                            {payment.invoice_number && (
                              <span>#{payment.invoice_number}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(payment.payment_date).toLocaleDateString('ar-AE')}
                            </span>
                            {payment.invoice_file_url && (
                              <a 
                                href={payment.invoice_file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                              >
                                <FileText className="h-3 w-3" />
                                {language === 'ar' ? 'الفاتورة' : 'Invoice'}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="text-left flex-shrink-0">
                          <p className="text-white font-bold text-lg">
                            {(payment.amount_aed || 0).toLocaleString('ar-AE', { minimumFractionDigits: 2 })} 
                            <span className="text-sm text-[#c9a227] mr-1">د.إ</span>
                          </p>
                          {payment.currency !== 'AED' && (
                            <p className="text-xs text-[#606060]">
                              {currency.symbol}{payment.amount?.toLocaleString()} × {payment.rate_used}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* View Details */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewInvoice(payment)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            title={language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePayment(payment.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            title={language === 'ar' ? 'حذف' : 'Delete'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
          )}

        </div>
      </main>


      {/* View Invoice Modal */}
      {showViewModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 pb-8 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1a1a2e] rounded-2xl border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: getServiceInfo(selectedPayment.service_type).bgColor }}
                  >
                    {(() => {
                      const ServiceIcon = getServiceInfo(selectedPayment.service_type).icon;
                      return <ServiceIcon className="h-6 w-6" style={{ color: getServiceInfo(selectedPayment.service_type).color }} />;
                    })()}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {language === 'ar' ? 'تفاصيل الفاتورة' : 'Invoice Details'}
                    </h2>
                    <p className="text-sm text-[#808080]">
                      {language === 'ar' ? getServiceInfo(selectedPayment.service_type).nameAr : getServiceInfo(selectedPayment.service_type).nameEn}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-[#808080] hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Invoice Details */}
              <div className="space-y-4">
                {/* Amount Card */}
                <div className="bg-gradient-to-r from-[#c9a227]/20 to-[#d4af37]/10 rounded-xl p-5 border border-[#c9a227]/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#c9a227] text-sm mb-1">
                        {language === 'ar' ? 'المبلغ بالدرهم' : 'Amount in AED'}
                      </p>
                      <p className="text-white text-3xl font-bold">
                        {(selectedPayment.amount_aed || 0).toLocaleString('ar-AE', { minimumFractionDigits: 2 })} 
                        <span className="text-lg text-[#c9a227] mr-1">د.إ</span>
                      </p>
                    </div>
                    {selectedPayment.currency !== 'AED' && (
                      <div className="text-left">
                        <p className="text-[#808080] text-sm mb-1">
                          {language === 'ar' ? 'المبلغ الأصلي' : 'Original Amount'}
                        </p>
                        <p className="text-white text-xl">
                          {getCurrencyInfo(selectedPayment.currency).symbol}
                          {selectedPayment.amount?.toLocaleString()}
                        </p>
                        <p className="text-xs text-[#606060]">
                          {language === 'ar' ? 'سعر الصرف:' : 'Rate:'} {selectedPayment.rate_used}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Paid To */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'مدفوع إلى' : 'Paid To'}</p>
                    <p className="text-white font-medium">
                      {language === 'ar' ? getServiceInfo(selectedPayment.service_type).nameAr : getServiceInfo(selectedPayment.service_type).nameEn}
                    </p>
                  </div>

                  {/* Currency */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'عملة الدفع' : 'Payment Currency'}</p>
                    <p className="text-white font-medium">
                      {getCurrencyInfo(selectedPayment.currency).symbol} {selectedPayment.currency}
                    </p>
                  </div>

                  {/* Reason */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'سبب الفاتورة' : 'Invoice Reason'}</p>
                    <p className="text-white font-medium">
                      {language === 'ar' ? (selectedPayment.reason_name_ar || selectedPayment.description) : (selectedPayment.reason_name_en || selectedPayment.description)}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'تاريخ الدفع' : 'Payment Date'}</p>
                    <p className="text-white font-medium">
                      {new Date(selectedPayment.payment_date).toLocaleDateString('ar-AE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  {/* Invoice Number */}
                  {selectedPayment.invoice_number && (
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                      <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number'}</p>
                      <p className="text-white font-medium">#{selectedPayment.invoice_number}</p>
                    </div>
                  )}

                  {/* Created Date */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-1">{language === 'ar' ? 'تاريخ الإضافة' : 'Created At'}</p>
                    <p className="text-white font-medium">
                      {new Date(selectedPayment.created_at).toLocaleDateString('ar-AE', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {/* Notes */}
                {selectedPayment.notes && (
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-2">{language === 'ar' ? 'ملاحظات' : 'Notes'}</p>
                    <p className="text-white">{selectedPayment.notes}</p>
                  </div>
                )}

                {/* Invoice File */}
                {selectedPayment.invoice_file_url && (
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <p className="text-[#808080] text-xs mb-3">{language === 'ar' ? 'ملف الفاتورة المرفق' : 'Attached Invoice File'}</p>
                    
                    {/* Preview if image */}
                    {(selectedPayment.invoice_file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                      <div className="mb-4 rounded-xl overflow-hidden border border-white/10">
                        <img 
                          src={selectedPayment.invoice_file_url} 
                          alt="Invoice" 
                          className="w-full max-h-64 object-contain bg-black/50"
                        />
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => window.open(selectedPayment.invoice_file_url, '_blank')}
                        variant="outline"
                        className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-xl"
                      >
                        <ExternalLink className="h-4 w-4 ml-2" />
                        {language === 'ar' ? 'فتح في نافذة جديدة' : 'Open in New Tab'}
                      </Button>
                      <Button
                        onClick={() => handleDownloadFile(
                          selectedPayment.invoice_file_url, 
                          `invoice_${selectedPayment.invoice_number || selectedPayment.id}`
                        )}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                      >
                        <Download className="h-4 w-4 ml-2" />
                        {language === 'ar' ? 'تحميل الفاتورة' : 'Download Invoice'}
                      </Button>
                    </div>
                  </div>
                )}

                {!selectedPayment.invoice_file_url && (
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5 text-center">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-[#606060]" />
                    <p className="text-[#808080] text-sm">
                      {language === 'ar' ? 'لا يوجد ملف فاتورة مرفق' : 'No invoice file attached'}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowViewModal(false)}
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-xl"
                >
                  {language === 'ar' ? 'إغلاق' : 'Close'}
                </Button>
                <Button
                  onClick={() => {
                    setShowViewModal(false);
                    deletePayment(selectedPayment.id);
                  }}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-xl"
                >
                  <Trash2 className="h-4 w-4 ml-2" />
                  {language === 'ar' ? 'حذف الفاتورة' : 'Delete Invoice'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}

